import React, { useCallback } from 'react'

import { useOverlayState, setChyron } from '../../../firebase/hooks/useOverlays'
import { useLastActiveMediaTrayCard } from '../../../firebase/hooks/useTray'
import { useRole } from '../../../global/blocs/roomInfo'
import { setPanelToAuto, usePanel } from '../../../firebase/hooks/usePanel'
import useSpeakersQuery from '../../../sharedLegacy/hooks/useSpeakersQuery'
import { useWebinarColors } from '../../../sharedLegacy/hooks/useWebinarColors'
import usePlaybackIntent from '../../../Webinar/hooks/usePlaybackIntent'
import useParticipantIntroOverlay from '../../../Webinar/hooks/useParticipantIntroOverlay'
import { PanelMonitor } from '../PanelMonitor/PanelMonitor'
import Participant from '../Participant/Participant'
import { ChyronType } from '../../../firebase/model/chyron-model'
import {
  useFloorState,
  switchToSolo,
  switchToSideBySide,
  switchToPanel,
} from '../../../firebase/hooks/useFloor'
import { useHumanParticipants } from '../../../webrtc/webRTCRoom'

import './ParticipantStrip.scss'

const ParticipantStrip = () => {
  const { data: speakers = [] } = useSpeakersQuery()

  const participants = useHumanParticipants()
  const { participantIds, screen } = usePanel()
  const role = useRole()
  const isProducer = role === 'producer'
  const { setPlaybackPauseIntent, setPlaybackResumeIntent } =
    usePlaybackIntent()
  const { setParticipantIntroOverlay } = useParticipantIntroOverlay()
  const overlay = useOverlayState()
  const lastActiveMediaTrayCard = useLastActiveMediaTrayCard()

  const webinarColors = useWebinarColors()

  const accentColor = webinarColors.accent.color

  const floor = useFloorState()

  const clearOtherSpeakerChyron = useCallback(
    (participantId) => {
      if (overlay?.type === ChyronType.Speaker) {
        const speakerFound = speakers.find(
          (speaker) => speaker.id === participantId
        )
        if (speakerFound) {
          const { name } = speakerFound
          if (name && name !== overlay.name) {
            setChyron(null)
          }
        }
      }
    },
    [overlay, speakers]
  )

  const handleSoloClick = useCallback(
    (participantId, identifier) => {
      if (!isProducer) return

      clearOtherSpeakerChyron(participantId)

      switchToSolo(participantId, identifier)

      if (identifier === 'camera') {
        setParticipantIntroOverlay(participantId)
      }

      setPlaybackPauseIntent()
    },
    [
      isProducer,
      setPlaybackPauseIntent,
      setParticipantIntroOverlay,
      clearOtherSpeakerChyron,
    ]
  )

  const handlePanelClick = useCallback(() => {
    switchToPanel()

    if (!screen && participantIds.length === 0) {
      setPanelToAuto()
    }

    setChyron(null)
    if (screen !== 'tray') {
      setPlaybackPauseIntent()
    } else if (lastActiveMediaTrayCard?.type === 'video') {
      setPlaybackResumeIntent()
    }
  }, [
    lastActiveMediaTrayCard?.type,
    participantIds,
    screen,
    setPlaybackPauseIntent,
    setPlaybackResumeIntent,
  ])

  const handleMixedModeClick = useCallback(
    (participantId) => {
      if (!isProducer) return

      clearOtherSpeakerChyron(participantId)

      switchToSideBySide(participantId, 'screenshare')

      setPlaybackPauseIntent()
    },
    [isProducer, setPlaybackPauseIntent, clearOtherSpeakerChyron]
  )

  return (
    <div className="participant-strip">
      {participants.map((participant) => (
        <Participant
          participantId={participant.id}
          key={participant.id}
          onSoloClick={handleSoloClick}
          onMixedModeClick={handleMixedModeClick}
        />
      ))}
      {isProducer && (
        <div className="panel-monitor-enclosure">
          <PanelMonitor
            tint={accentColor}
            isSelected={floor?.layout === 'panel'}
            onContentClick={handlePanelClick}
          />
        </div>
      )}
    </div>
  )
}

export { ParticipantStrip }
